import { HttpClient, HttpErrorResponse, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { RxStomp, RxStompConfig, RxStompState } from '@stomp/rx-stomp';
import { Frame } from '@stomp/stompjs';
import {
  BehaviorSubject,
  combineLatest,
  firstValueFrom,
  merge,
  Observable,
  Subject,
  throwError,
  TimeoutError,
} from 'rxjs';
import { catchError, filter, map, mergeMap, retryWhen, shareReplay, startWith, tap, timeout } from 'rxjs/operators';
import SockJS from 'sockjs-client';
import { TokenService } from '../../auth/services/token.service';
import { ErrorDialogComponent } from '../../shared/components/error-dialog/error-dialog.component';
import { AuthState } from '../../shared/models/auth/auth-state.model';
import { AuthStoreService } from '../../shared/services/auth/auth-store.service';
import { AppConfig } from './app-config.service';
import { MaintenanceService } from './maintenance.service';
import { TopUpInfoDialogComponent } from '../../topup/components/topup-info-dialog/topup-info-dialog.component';
import { MAX_WIDTH_90, WIDTH_480, WIDTH_560 } from '../../shared/configs/dialog-config';
import { TopUpPaymentError } from '../../topup/models/topup-model';
import { HttpOptions } from '../models/http-options.model';
import { ErrorCode } from '../models/error-code.enum';
import { DialogService } from './dialog.service';
import { CsrfStoreService } from '../../auth/services/csrf-store.service';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private timeout = 15000;
  private paymentTimeout = 60000;
  private csrfToken: CsrfToken;
  private stompClient: RxStomp;
  public stompClientStatus$: BehaviorSubject<RxStompState>;
  private isWsConfigured = false;

  private reconnect = new Subject();

  constructor(
    private appConfig: AppConfig,
    private authStoreService: AuthStoreService,
    private http: HttpClient,
    private dialog: MatDialog,
    private maintenanceService: MaintenanceService,
    private router: Router,
    private tokenService: TokenService,
    private dialogService: DialogService,
    private csrfService: CsrfStoreService,
  ) {
    this.stompClient = new RxStomp();
    this.stompClientStatus$ = this.stompClient.connectionState$;
  }

  public init(): void {
    this.http
      .get<ApiInfo>(`${this.appConfig.config.healthCheck.actuatorUrl}/info`)
      .subscribe((info: ApiInfo) =>
        console.log(`Api version: ${info.version} build: ${info.buildNumber} ${info.commitHash}`),
      );

    this.authStoreService.state$
      .pipe(
        filter((state: AuthState) => state === AuthState.NOT_AUTHENTICATED),
        filter(() => this.stompClient && this.stompClient.connected()),
        tap(() => this.stompClient.deactivate()),
      )
      .subscribe();
    merge(
      this.authStoreService.state$.pipe(filter((state: AuthState) => state === AuthState.AUTHENTICATED)),
      this.reconnect,
    )
      .pipe(
        filter(() => this.authStoreService.isAuthenticated),
        filter(() => !this.appConfig.config.demo),
        filter(() => !this.stompClient.connected()),
        tap(() => this.connectWebSocket()),
      )
      .subscribe();
  }

  public async connectWebSocket() {
    let csrfHeaderName: string | undefined;
    let csrfTokenValue: string | undefined;
    ;

    if (this.appConfig.config.csrfEnabled) {
      const csrf = await firstValueFrom(this.getCsrfToken(true));
      this.csrfService.setState(csrf);
      csrfHeaderName = csrf.headerName || 'X-XSRF-TOKEN';
      csrfTokenValue = csrf.token;
    }

    const connectHeaders: Record<string, string> = {
      Authorization: this.tokenService.getToken(),
    };

    if (this.appConfig.config.csrfEnabled && csrfHeaderName && csrfTokenValue) {
      connectHeaders[csrfHeaderName] = csrfTokenValue;
      connectHeaders['X-XSRF-TOKEN'] = csrfTokenValue;
    }

    const stompConfig: RxStompConfig = {
      connectHeaders,
      reconnectDelay: 4000,
      heartbeatIncoming: 10000,
      heartbeatOutgoing: 10000,
      beforeConnect: async () => {
        if (this.appConfig.config.csrfEnabled) {
          const csrf = await firstValueFrom(this.getCsrfToken(true));
          this.csrfService.setState(csrf);
          const hName = csrf.headerName || 'X-XSRF-TOKEN';
          this.stompClient.configure({
            connectHeaders: {
              Authorization: this.tokenService.getToken(),
              [hName]: csrf.token,
              'X-XSRF-TOKEN': csrf.token,
            },
          });
        } else {
          this.stompClient.configure({
            connectHeaders: { Authorization: this.tokenService.getToken() },
          });
        }
      },
      webSocketFactory: () => new SockJS(this.appConfig.config.wsUrl),
    };

    if (!this.isWsConfigured) {
      this.stompClient.configure(stompConfig);
      this.isWsConfigured = true;

      this.stompClient.webSocketErrors$.subscribe(err => {
        console.error('WebSocket handshake/XHR error:', err);
      });
      this.stompClient.stompErrors$.subscribe(frame => {
        console.error('STOMP error frame:', frame);
      });
      this.stompClient.connectionState$.subscribe(s => {
        // console.log('State:', s);
      });

      this.stompClient.connected$.subscribe(() => {
        console.log('✅ WebSocket STOMP połączony');
      });
    } else {
      await this.stompClient.deactivate();
      this.stompClient.configure(stompConfig);
    }

    this.stompClient.activate();

  }

  private getCsrfToken(refresh?: boolean): Observable<CsrfToken> {
    if (this.csrfService.state === null || refresh) {
      return this.http.get<CsrfToken>(this.appConfig.config.apiUrl + '/csrf', {
        withCredentials: true,
        responseType: 'json',
        observe: 'body',
      }).pipe(
        tap(value => this.csrfService.setState(value)),
      );
    } else {
      return this.csrfService.state$;
    }
  }

  public get<T>(
    path: string,
    params?: HttpParams | { [p: string]: string | string[] },
    subscribe: boolean = false,
    url: string = '',
    fn = this.matcher,
    retryEnabled: boolean = true,
  ): Observable<T> {
    const fetch = this.fetch<T>(path, params, retryEnabled);

    if (subscribe) {
      const splittedPath = path.split('/');
      const subscribeUrl = url || splittedPath[splittedPath.length - 1];

      return combineLatest([
        this.fetch<T>(path, params).pipe(shareReplay({ bufferSize: 1, refCount: true })),
        this.subscribe<T>(subscribeUrl).pipe(startWith('')),
      ]).pipe(map(fn));
    } else {
      return fetch;
    }
  }

  public post<T>(path: string, body?: any, options?: HttpOptions, retryEnabled: boolean = true): Observable<T> {
    return this.http.post<T>(this.appConfig.config.apiUrl + path, body, options).pipe(
      timeout(this.timeout),
      retryWhen(this.retryStrategy(retryEnabled)),
      catchError(async (err) => await this.handleError<T>(err)),
    );
  }

  public postPayment<T>(
    path: string,
    body?: any,
    params?: HttpParams | { [p: string]: string | string[] },
  ): Observable<T> {
    return this.http
      .post<T>(this.appConfig.config.apiUrl + path, body, { params })
      .pipe(
        timeout(this.paymentTimeout),
        retryWhen(this.retryStrategy(false)),
        catchError(async (err) => await this.handleError<T>(err)),
      );
  }

  public put<T>(path: string, body?: any, params?: HttpParams | { [p: string]: string | string[] }): Observable<T> {
    return this.http
      .put<T>(this.appConfig.config.apiUrl + path, body, { params })
      .pipe(
        timeout(this.timeout),
        retryWhen(this.retryStrategy()),
        catchError(async (err) => await this.handleError<T>(err)),
      );
  }

  public patch<T>(path: string, body?: any, params?: HttpParams | { [p: string]: string | string[] }): Observable<T> {
    return this.http
      .patch<T>(this.appConfig.config.apiUrl + path, body, { params })
      .pipe(
        timeout(this.timeout),
        retryWhen(this.retryStrategy()),
        catchError(async (err) => await this.handleError<T>(err)),
      );
  }

  public delete<T>(path: string, body?: any, params?: HttpParams | { [p: string]: string | string[] }): Observable<T> {
    return this.http
      .delete<T>(this.appConfig.config.apiUrl + path, { params })
      .pipe(
        timeout(this.timeout),
        retryWhen(this.retryStrategy()),
        catchError(async (err) => await this.handleError<T>(err)),
      );
  }

  public async downloadFile(path: string, params?: HttpParams | {
    [p: string]: string | string[]
  }): Promise<HttpResponse<Blob>> {
    return await this.fetchFile(path, params)
      .toPromise();
  }

  public fetchFile(path: string, params?: HttpParams | {
    [p: string]: string | string[]
  }): Observable<HttpResponse<Blob>> {
    return this.http
      .get<Blob>(this.appConfig.config.apiUrl + path, {
        params: params,
        responseType: 'blob' as 'json',
        observe: 'response',
      })
      .pipe(
        retryWhen(this.retryStrategy()),
        catchError((err) => this.handleError<HttpResponse<Blob>>(err)),
      );
  }

  public async uploadFile(path: string, file: File): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    return await this.http
      .post(this.appConfig.config.apiUrl + path, formData)
      .pipe(
        retryWhen(this.retryStrategy()),
        catchError((err) => this.handleError(err)),
      )
      .toPromise();
  }

  public async uploadFiles(path: string, files: File[]): Promise<any> {
    const formData = new FormData();
    files.forEach((file: File) => formData.append('files', file));
    return this.http
      .post(this.appConfig.config.apiUrl + path, formData)
      .pipe(
        retryWhen(this.retryStrategy()),
        catchError((err) => this.handleError(err)),
      )
      .toPromise();
  }

  public subscribe<T>(url: string = ''): Observable<T> {
    return this.stompClient.watch(`/user/topic/${url}`)
      .pipe(map((frame: Frame) => JSON.parse(JSON.parse(frame.body))));
  }

  public fetch<T>(
    path: string,
    params?: HttpParams | { [p: string]: string | string[] },
    retryEnabled: boolean = true,
  ): Observable<T> {
    return this.http
      .get<T>(this.appConfig.config.apiUrl + path, { params })
      .pipe(
        timeout(this.timeout),
        retryWhen(this.retryStrategy(retryEnabled)),
        catchError((err) => this.handleError<T>(err)),
      );
  }

  private retryStrategy(retryEnabled: boolean = true): any {
    return (attempts: Observable<any>) =>
      attempts.pipe(
        mergeMap((error, i) => {
          const retryAttempt = i + 1;
          console.log('retry' + i);
          if (error instanceof TimeoutError && retryEnabled) {
            console.log(`Retry strategy - request timed out after ${this.timeout}ms`);
          } else if (retryAttempt > 3 || error.status !== 401) {
            return throwError(error);
          }

          return this.authStoreService.refreshToken();
        }),
      );
  }

  private async handleError<T>(error: any): Promise<T> {
    if (error instanceof HttpErrorResponse) {
      await this.handleHttpErrorResponse(error);
    } else if (error instanceof TimeoutError) {
      console.log(`Request timed out after ${this.timeout}ms`);
    } else if (error === ErrorCode.READ_ONLY) {
      this.dialogService.openReadOnlyInfoDialog();
    } else {
      this.dialog.open(ErrorDialogComponent, {
        closeOnNavigation: true,
        data: error,
        disableClose: false,
        width: WIDTH_480,
        maxWidth: MAX_WIDTH_90,
      });
    }
    throw error;
  }

  private async handleHttpErrorResponse(error: HttpErrorResponse): Promise<any> {
    if (await this.maintenanceService.isUnderMaintenance()) {
      return;
    }
    if (error.status === 401 || error.status === 400) {
      console.error('Token invalid or timed out!', this.tokenService.getToken());
      this.router.navigateByUrl('/auth/login');
    } else if (error.status === 0) {
      console.error('No internet connection');
    } else if (error.status === 500) {
      this.dialog.open(ErrorDialogComponent, {
        closeOnNavigation: true,
        data: error,
        disableClose: false,
        width: WIDTH_480,
        maxWidth: MAX_WIDTH_90,
      });
    } else if (error.status === 422 && TopUpPaymentError[error?.error?.errorCode]) {
      this.dialog
        .open(TopUpInfoDialogComponent, {
          data: error?.error?.errorCode,
          width: WIDTH_560,
          maxWidth: MAX_WIDTH_90,
          disableClose: false,
        })
        .afterClosed();
    }
  }

  private matcher = ([data, socketFrame]) => {
    if (socketFrame) {
      if (Array.isArray(data)) {
        return data.map((item) => {
          if (item.id === socketFrame.id) {
            return socketFrame;
          } else {
            return item;
          }
        });
      } else {
        if (data.id === socketFrame.id) {
          return socketFrame;
        } else {
          return data;
        }
      }
    } else {
      return data;
    }
  };
}

export class CsrfToken {
  headerName: string;
  parameterName: string;
  token: string;
}

export class CommandResult<T> {
  constructor(readonly type: CommandResultType, readonly payload: T) {
  }
}

export type CommandResultType = 'ACCEPTED' | 'CREATED' | 'FAILED';

class ApiInfo {
  public version: string;
  public buildNumber: string;
  public commitHash: string;
}
